import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { Leave } from '../classes/leave';
import { LeaveUserDate } from '../classes/leave-user-date';
import { Realisation, RealisationHourtype } from '../classes/realisation';
import { User } from '../classes/user.class';
import { Settings } from '../settings.class';
import { Utils } from '../utils.class';
import { ApiResponse, ApiService } from './api/api.service';
import { AuthorisationService } from './auth/authorisation.service';
import { Features } from './auth/features';
import { RealtimeService } from './realtime/realtime.service';
import { WebsocketService } from './websocket/websocket.service';

@Injectable({
    providedIn: 'root',
})
export class LeaveService extends RealtimeService<Leave> {
    constructor(
        public apiService: ApiService,
        protected websocketService: WebsocketService
    ) {
        super(websocketService, Features.Leave);
    }

    itemInDaterange(leave: Leave, fromDate: Date, toDate: Date) {
        return (
            Utils.getTimeOrNull(leave.begindate) <= Utils.getTimeOrNull(toDate) &&
            Utils.getTimeOrNull(leave.enddate) >= Utils.getTimeOrNull(fromDate)
        );
    }

    getLeaves(history = false) {
        if (!AuthorisationService.hasFeature(Features.Leave)) {
            return of({ data: [], success: false } as ApiResponse<Leave[]>);
        }

        return this.apiService.getCall$<Leave[]>('leave', { history });
    }

    getLeave(id: number) {
        return this.apiService.getCall$<Leave>(`leave/${id}`);
    }

    getLeaveRealisations(id: number) {
        return this.apiService.getCall$<Realisation[]>(`leave/realisations/${id}`);
    }

    saveLeave(leave: Omit<Leave, 'users'> & { users: number[] }) {
        return this.apiService.postCall$<Leave>('leave', leave);
    }

    deleteLeave(id) {
        return this.apiService.deleteCall$<Leave>(`leave/${id}`);
    }

    dayLeaves(leaves: Leave[], users: User[], day: Date): { leave: Leave; users: User[] }[] {
        const leavesAndUsers = [] as { leave: Leave; users: User[] }[];
        const startOfDay = Utils.setTime(new Date(day), 0, 0);
        const endOfDay = Utils.setTime(new Date(day), 23, 59);

        leaves
            .filter(
                leave =>
                    Utils.getTimeOrNull(startOfDay) <= Utils.getTimeOrNull(leave.enddate) &&
                    Utils.getTimeOrNull(endOfDay) >= Utils.getTimeOrNull(leave.begindate) &&
                    !leave.deleted_at
            )
            .forEach(leave => {
                const leaveDates = this.generateLeaveDates(leave);
                const dayString = formatDate(day, 'yyyy-MM-dd', 'nl');
                if (
                    !leaveDates.some(
                        dates =>
                            formatDate(dates.beginDate, 'yyyy-MM-dd', 'nl') === dayString ||
                            formatDate(dates.endDate, 'yyyy-MM-dd', 'nl') === dayString
                    )
                ) {
                    return;
                }

                const userIds = leave.users.map(u => u.id);

                const leaveAndUser = { leave, users: [] };

                users
                    .filter(u => (leave.common ? true : userIds.includes(u.id)))
                    .forEach(user => {
                        leaveDates.forEach(dates => {
                            const dateString = formatDate(dates.beginDate, 'yyyy-MM-dd', 'nl');

                            const excludedLeaveUserFound = leave.excluded.find(
                                e => e.user_id === user.id && formatDate(e.date, 'yyyy-MM-dd', 'nl') === dateString
                            );

                            const leaveHasSomeUser = leaveAndUser.users.some(u => u.id === user.id);

                            if (!excludedLeaveUserFound && !leaveHasSomeUser) {
                                leaveAndUser.users.push(user);
                            }
                        });
                    });

                if (leaveAndUser.users.length > 0) {
                    leavesAndUsers.push(leaveAndUser);
                }
            });

        return leavesAndUsers;
    }

    getByTypeAndDates(leaves: Leave[], fromDate: Date, toDate: Date, hourtype = RealisationHourtype.not_available) {
        return leaves.find(
            leave =>
                leave.hourtype === hourtype &&
                leave.deleted_at === null &&
                Utils.getTimeOrNull(fromDate) <= Utils.getTimeOrNull(leave.enddate) &&
                Utils.getTimeOrNull(toDate) >= Utils.getTimeOrNull(leave.begindate)
        );
    }

    getLeavesForUserOnDate(leaves, userId, beginDate: Date, endDate: Date, hoursExpanded = 0): LeaveUserDate[] {
        return leaves
            ?.filter(l => l.common || l.users.map(u => u.id).includes(userId))
            .flatMap(l => {
                const leaveDates = this.generateLeaveDates(l);
                return leaveDates
                    .filter(leaveDate => {
                        const dateString = Utils.formatDate(leaveDate.beginDate);
                        const isExcluded = l.excluded.find(
                            e => e.user_id === userId && Utils.formatDate(e.date) === dateString
                        );
                        const stampExpanded = hoursExpanded * 60 * 60 * 1000;
                        const leaveDateMatches =
                            leaveDate.endDate >= Utils.getTimeOrNull(beginDate) - stampExpanded &&
                            leaveDate.beginDate <= Utils.getTimeOrNull(endDate) + stampExpanded;
                        return !isExcluded && leaveDateMatches;
                    })
                    .map(leaveDate => {
                        return {
                            leave: l,
                            date: leaveDate,
                            userId: userId,
                            blocking:
                                l.close_period ||
                                (leaveDate.endDate > Utils.newDate(beginDate) &&
                                    leaveDate.beginDate < Utils.newDate(endDate)),
                        };
                    });
            });
    }

    generateLeaveDates(leave: Leave): { beginDate; endDate }[] {
        const begintime = new Date();
        if (!leave.begintime) {
            Utils.setTime(begintime, Settings.DEFAULT_START, 0, 0);
        } else {
            const time = leave.begintime.split(':');
            Utils.setTime(begintime, +time[0], +time[1], 0);
        }
        const endtime = new Date();
        if (!leave.endtime) {
            Utils.setTime(endtime, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);
        } else {
            const time = leave.endtime.split(':');
            Utils.setTime(endtime, +time[0], +time[1], 0);
        }

        const dates: { beginDate: Date; endDate: Date }[] = [];
        const loopDate = new Date(leave.begindate);
        const lastEndDate = new Date(leave.enddate);
        const firstWeek = +formatDate(loopDate, 'w', 'nl');
        const startTime = new Date(leave.begindate);
        Utils.setTime(startTime, begintime.getHours(), begintime.getMinutes());
        const endTime = new Date(leave.enddate);
        Utils.setTime(endTime, endtime.getHours(), endtime.getMinutes());

        const loopEndDate = new Date(leave.enddate);
        while (loopDate.getTime() < loopEndDate.getTime()) {
            let generateThisDay = !(leave.interval_week > 0);
            if (leave.interval_week > 0) {
                const week = +formatDate(loopDate, 'w', 'nl');
                const thisWeek = (week - firstWeek) % leave.interval_week === 0;
                const weekDayName = formatDate(loopDate, 'EEEE', 'en').toLowerCase();
                const thisDay = leave[`repeat_${weekDayName}`];
                generateThisDay = (thisWeek && thisDay) || leave.close_period;
            }

            if (generateThisDay && leave.close_period) {
                dates.push({
                    beginDate: Utils.setTime(new Date(loopDate), 0, 0, 0),
                    endDate: Utils.setTime(new Date(loopDate), 23, 59, 59),
                });
            } else if (generateThisDay) {
                const beginDate = new Date(loopDate);
                if (
                    beginDate.getTime() === Utils.getTimeOrNull(leave.begindate) &&
                    beginDate.getTime() > startTime.getTime()
                ) {
                    Utils.setTime(
                        beginDate,
                        new Date(leave.begindate).getHours(),
                        new Date(leave.begindate).getMinutes(),
                        0
                    );
                } else {
                    Utils.setTime(beginDate, startTime.getHours(), startTime.getMinutes(), 0);
                }

                const endDate = new Date(loopDate);
                Utils.setTime(endDate, new Date(leave.enddate).getHours(), new Date(leave.enddate).getMinutes(), 0);

                if (endDate.getTime() !== lastEndDate.getTime() || lastEndDate.getTime() > endTime.getTime()) {
                    Utils.setTime(endDate, endTime.getHours(), endTime.getMinutes(), 0);
                }

                const realisation = { beginDate, endDate };
                dates.push(realisation);
            }
            loopDate.setDate(loopDate.getDate() + 1);
        }
        return dates;
    }

    getExecutorLeaves(startDate?: string, endDate?: string) {
        return this.apiService.getCallObs<ApiResponse<Leave[]>>('leave/executor', {
            fromDate: startDate,
            toDate: endDate,
        });
    }
}
